import CancelIcon from "@mui/icons-material/Clear";
import IndeterminateCheckBoxIcon from "@mui/icons-material/IndeterminateCheckBox";
import SearchIcon from "@mui/icons-material/Search";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import FormControl from "@mui/material/FormControl";
import FormHelperText from "@mui/material/FormHelperText";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import InputLabel from "@mui/material/InputLabel";
import ListItemText from "@mui/material/ListItemText";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import { makeStyles } from "@mui/styles";
import PropTypes from "prop-types";
import React from "react";
import { ACQUIRED_SHIFT, LAST_UPDATED, STATUS, TRADE_TYPE } from "../util/constants.js";
import SlideUpTransition from "./fundamentals/SlideUpTransition";
import TradeDialog from "./TradeDialog.jsx";

const useStyles = makeStyles((theme) => ({
  filterButton: {
    marginTop: "10px",
    width: "88%",
  },
  title: {
    fontSize: "24px",
    textAlign: "left",
    width: "210px",
    margin: "auto",
    marginTop: "10px",
    marginLeft: "20px",
    whiteSpace: "normal",
  },
  searchInput: {
    width: "47vw",
    left: "5px",
  },
  sortInput: {
    width: "47vw",
    right: "5px",
  },
}));

const TradeFilterFormMobile = (props) => {
  const classes = useStyles();

  const renderSortByMenuItems = () => {
    const dateTypes = [TRADE_TYPE, STATUS, LAST_UPDATED, ACQUIRED_SHIFT];
    return dateTypes
      .slice()
      .sort()
      .map((dateType) => {
        return (
          <MenuItem key={dateType} value={dateType}>
            {dateType}
          </MenuItem>
        );
      });
  };

  const renderTradeTypeFilterMenuItems = () => {
    return props.allTradeTypeFilters
      .slice()
      .sort()
      .map((tradeType) => {
        return (
          <MenuItem key={tradeType} value={tradeType}>
            <Checkbox color="primary" checked={props.tradeTypeFilters.indexOf(tradeType) > -1} />
            <ListItemText primary={tradeType} />
          </MenuItem>
        );
      });
  };

  const renderStatusFilterMenuItems = () => {
    return props.allStatusFilters
      .slice()
      .sort()
      .map((status) => {
        return (
          <MenuItem key={status} value={status}>
            <Checkbox color="primary" checked={props.statusFilters.indexOf(status) > -1} />
            <ListItemText primary={status} />
          </MenuItem>
        );
      });
  };

  const renderTradeFilterDialog = () => {
    return (
      <Dialog
        fullScreen
        className="detail-dialog"
        fullWidth
        maxWidth="md"
        open={props.isMobileFilterDialogOpen}
        TransitionComponent={SlideUpTransition}
        keepMounted
        onClose={props.onMobileFilterDialogClose.bind(this)}
        aria-labelledby="alert-dialog-slide-title"
        aria-describedby="alert-dialog-slide-description"
        data-testid="trade-filter-form-mobile"
      >
        <DialogTitle id="alert-dialog-slide-title" className="detail-dialog-title">
          Filters
        </DialogTitle>
        <DialogContent className="detail-dialog-content">
          <DialogContentText component={"div"} id="alert-dialog-slide-description">
            <Grid container>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {props.tradeTypeFilters.length > 0 ? (
                    <FormControl variant="outlined">
                      <InputLabel>Trade Type</InputLabel>
                      <Select
                        id="tradeFormTypeSelectElement"
                        className="filter-input"
                        label="Trade Type"
                        multiple={true}
                        value={props.tradeTypeFilters}
                        onChange={props.onTradeTypeFilterChange.bind(this)}
                        renderValue={(selected) =>
                          selected.length === props.allTradeTypeFilters.length
                            ? "ALL"
                            : selected.length === 1
                            ? selected[0]
                            : "MULTI"
                        }
                      >
                        <MenuItem disabled>
                          <em>Filter on Trade Type</em>
                        </MenuItem>
                        <MenuItem key="select-all" value="Select All">
                          <Checkbox
                            color="primary"
                            checked={props.isTradeTypeSelectAllChecked()}
                            indeterminate={props.isTradeTypeSelectAllIndeterminate()}
                            indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {renderTradeTypeFilterMenuItems()}
                      </Select>
                    </FormControl>
                  ) : (
                    <FormControl variant="outlined">
                      <InputLabel>Trade Type</InputLabel>
                      <Select
                        error
                        id="tradeFormTypeSelectElement"
                        className="filter-input"
                        label="Trade Type"
                        multiple={true}
                        value={props.tradeTypeFilters}
                        onChange={props.onTradeTypeFilterChange.bind(this)}
                        renderValue={(selected) =>
                          selected.length === props.allTradeTypeFilters.length
                            ? "ALL"
                            : selected.length === 1
                            ? selected[0]
                            : "MULTI"
                        }
                      >
                        <MenuItem disabled>
                          <em>Filter on Trade Type</em>
                        </MenuItem>
                        <MenuItem key="select-all" value="Select All">
                          <Checkbox
                            color="primary"
                            checked={props.isTradeTypeSelectAllChecked()}
                            indeterminate={props.isTradeTypeSelectAllIndeterminate()}
                            indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {renderTradeTypeFilterMenuItems()}
                      </Select>
                      <FormHelperText error>At least one Trade Type must be selected</FormHelperText>
                    </FormControl>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box mt={1} mb={1}>
                  {props.statusFilters.length > 0 ? (
                    <FormControl variant="outlined">
                      <InputLabel>Status</InputLabel>
                      <Select
                        id="statusFilterSelectElement"
                        className="filter-input"
                        label="Status"
                        multiple={true}
                        value={props.statusFilters}
                        MenuProps={{ autoFocus: false }}
                        onChange={props.onStatusFilterChange.bind(this)}
                        renderValue={(selected) =>
                          selected.length === props.allStatusFilters.length
                            ? "ALL"
                            : selected.length === 1
                            ? selected[0]
                            : "MULTI"
                        }
                      >
                        <MenuItem disabled>
                          <em>Filter on Status</em>
                        </MenuItem>
                        <MenuItem key="select-all" value="Select All">
                          <Checkbox
                            color="primary"
                            checked={props.isStatusSelectAllChecked()}
                            indeterminate={props.isStatusSelectAllIndeterminate()}
                            indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {renderStatusFilterMenuItems()}
                      </Select>
                    </FormControl>
                  ) : (
                    <FormControl variant="outlined">
                      <InputLabel>Status</InputLabel>
                      <Select
                        error
                        id="statusFilterSelectElement"
                        className="filter-input"
                        label="Status"
                        multiple={true}
                        value={props.statusFilters}
                        MenuProps={{ autoFocus: false }}
                        onChange={props.onStatusFilterChange.bind(this)}
                        renderValue={(selected) =>
                          selected.length === props.allStatusFilters.length
                            ? "ALL"
                            : selected.length === 1
                            ? selected[0]
                            : "MULTI"
                        }
                      >
                        <MenuItem disabled>
                          <em>Filter on Status</em>
                        </MenuItem>
                        <MenuItem key="select-all" value="Select All">
                          <Checkbox
                            color="primary"
                            checked={props.isStatusSelectAllChecked()}
                            indeterminate={props.isStatusSelectAllIndeterminate()}
                            indeterminateIcon={<IndeterminateCheckBoxIcon color="primary" />}
                          />
                          <ListItemText primary="Select All" />
                        </MenuItem>
                        {renderStatusFilterMenuItems()}
                      </Select>
                      <FormHelperText error>At least one Status must be selected</FormHelperText>
                    </FormControl>
                  )}
                </Box>
              </Grid>
            </Grid>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            className="dialog-left-button"
            variant="outlined"
            color="primary"
            onClick={props.onMobileFilterDialogClose.bind(this)}
          >
            Cancel
          </Button>
          <Button
            className="dialog-right-button-2"
            variant="contained"
            color="primary"
            onClick={props.onClickMobileFilterDialogApplyFiltersButton.bind(this)}
          >
            Apply Filters
          </Button>
        </DialogActions>
      </Dialog>
    );
  };

  const SearchButton = () => {
    return (
      <IconButton
        size="small"
        className="search-icon-button"
        onMouseDown={props.onSearchSubmit.bind(this, props.searchString)}
      >
        <SearchIcon />
      </IconButton>
    );
  };
  const CancelButton = () => {
    return (
      <IconButton size="small" className="cancel-icon-button" onMouseDown={props.onSearchCancel.bind(this)}>
        <CancelIcon />
      </IconButton>
    );
  };
  return (
    <Grid container justifyContent="center" spacing={1}>
      <Grid container className="mobile-title-row">
        <Grid item xs={6}>
          <Typography component={"div"}>
            <Box className={classes.title}>{props.title}</Box>
          </Typography>
        </Grid>
        <Grid item xs={6}>
          <Button
            className={classes.filterButton}
            variant="contained"
            color="primary"
            onClick={props.onClickMobileFilterDialogOpen.bind(this)}
          >
            Filters
          </Button>
        </Grid>
      </Grid>

      <Grid item xs={6}>
        <Box mt={1} mb={1}>
          <TextField
            className={classes.searchInput}
            label="Search"
            variant="outlined"
            value={props.searchString}
            onChange={props.onSearchChange.bind(this)}
            onBlur={props.onSearchBlur.bind(this)}
            InputProps={{
              endAdornment:
                props.searchString !== "" ? (
                  <InputAdornment position="end">
                    {props.tradeFoundByReferenceNumberSearchIsLoading ? <CircularProgress size={16} /> : ""}
                    <CancelButton />
                    <SearchButton />
                  </InputAdornment>
                ) : (
                  <InputAdornment position="end">
                    <SearchButton />
                  </InputAdornment>
                ),
            }}
            onKeyUp={props.onSearchEnterKeyPressed.bind(this)}
            error={props.searchStringErrors !== ""}
            helperText={props.searchStringErrors}
          />
        </Box>
      </Grid>
      <Grid item xs={6}>
        <Box mt={1} mb={1}>
          <FormControl variant="outlined">
            <InputLabel>Sort By</InputLabel>
            <Select
              id="tradeFormTypeSelectElement"
              className={classes.sortInput}
              value={props.mobileSortByValue}
              onChange={props.onMobileSortByChange.bind(this)}
              label="Sort By"
            >
              <MenuItem disabled>
                <em>Sort By</em>
              </MenuItem>
              {renderSortByMenuItems()}
            </Select>
          </FormControl>
        </Box>
      </Grid>
      {renderTradeFilterDialog()}
      {props.tradeFoundByReferenceNumberSearchIsDialogOpen && !props.tradeFoundByReferenceNumberSearchIsLoading ? (
        <TradeDialog
          loggedInUserData={props.loggedInUserData}
          trade={props.tradeFoundByReferenceNumberSearch}
          isDialogOpen={props.tradeFoundByReferenceNumberSearchIsDialogOpen}
          handleClickDialogOpen={props.onClickTradeFoundByReferenceNumberSearchDialogOpen.bind(this)}
          handleClickDialogClose={props.onClickTradeFoundByReferenceNumberSearchDialogClose.bind(this)}
          isAdminPage={props.isAdminPage}
          eid={props.loggedInUserData.eid}
          isDesktopView={props.isDesktopView}
        />
      ) : (
        ""
      )}
    </Grid>
  );
};

TradeFilterFormMobile.propTypes = {
  loggedInUserData: PropTypes.object.isRequired,
  isAdminPage: PropTypes.bool.isRequired,
  title: PropTypes.string.isRequired,
  trades: PropTypes.array.isRequired,
  tradeTypeFilters: PropTypes.array.isRequired,
  allTradeTypeFilters: PropTypes.array.isRequired,
  onTradeTypeFilterChange: PropTypes.func.isRequired,
  isTradeTypeSelectAllChecked: PropTypes.func.isRequired,
  isTradeTypeSelectAllIndeterminate: PropTypes.func.isRequired,
  statusFilters: PropTypes.array.isRequired,
  allStatusFilters: PropTypes.array.isRequired,
  onStatusFilterChange: PropTypes.func.isRequired,
  isStatusSelectAllChecked: PropTypes.func.isRequired,
  isStatusSelectAllIndeterminate: PropTypes.func.isRequired,
  searchString: PropTypes.string.isRequired,
  searchStringErrors: PropTypes.string.isRequired,
  tradeFoundByReferenceNumberSearch: PropTypes.object,
  tradeFoundByReferenceNumberSearchIsLoading: PropTypes.bool.isRequired,
  tradeFoundByReferenceNumberSearchIsDialogOpen: PropTypes.bool.isRequired,
  onClickTradeFoundByReferenceNumberSearchDialogOpen: PropTypes.func.isRequired,
  onClickTradeFoundByReferenceNumberSearchDialogClose: PropTypes.func.isRequired,
  onSearchChange: PropTypes.func.isRequired,
  onSearchBlur: PropTypes.func.isRequired,
  onSearchEnterKeyPressed: PropTypes.func.isRequired,
  onSearchCancel: PropTypes.func.isRequired,
  onSearchSubmit: PropTypes.func.isRequired,
  mobileSortByValue: PropTypes.string.isRequired,
  onMobileSortByChange: PropTypes.func.isRequired,
  onMobileFilterDialogClose: PropTypes.func.isRequired,
  isMobileFilterDialogOpen: PropTypes.bool.isRequired,
  onClickMobileFilterDialogOpen: PropTypes.func.isRequired,
  onClickMobileFilterDialogApplyFiltersButton: PropTypes.func.isRequired,
};

export default TradeFilterFormMobile;
